import { Component, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { FullscreenImageViewerComponent } from '../model-image-fullscreen-view/fullscreen-image-viewer.component';
import { ModelService } from '../model.service';
import { Model } from '../../models/model';
import { ActivatedRoute } from '@angular/router';
import { MediaService } from '../../media/media.service';
import { Image } from '../../models/image';
import { Video } from '../../models/video';
import { Subject, EMPTY, Observable } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { LikesService } from '../../likes/likes.service';
import { ViewsService } from '../../views/views.service';
import { AuthService } from '../../auth/auth.service';
import { UserService } from '../../auth/user.service';
import { User } from '../../models/user';

@Component({
  selector: 'app-model-detail',
  templateUrl: './model-detail.component.html',
  styleUrl: './model-detail.component.css',
})


export class ModelDetailComponent implements OnDestroy {
  model: Model | null = null;
  dataLoading: boolean = true;
  videos: Video[] = [];
  images: Image[] = [];
  imageLikedStates: { [id: string]: Observable<boolean> } = {};
  videoLikedStates: { [id: string]: Observable<boolean> } = {};
  tags: string[] = [];
  displayedTags: string[] = [];
  showAllTags = false;

  imageLoading: boolean[] = [];
  videoLoading: boolean[] = [];
  videoPlaying: boolean[] = [];

  isMobile: boolean = false;
  isLiked$: Observable<boolean> | null = null;

  /** Whether the current user is viewing their own profile */
  isOwnProfile = false;

  /** Current user data for onboarding state */
  currentUser: User | null = null;

  /** Whether to show the onboarding complete banner */
  showOnboardingBanner = false;

  /** Whether model onboarding is complete (hides checklist) */
  isModelOnboardingComplete = false;

  /** Whether user onboarding data has been loaded (prevents rendering before data is ready) */
  userOnboardingLoaded = false;

  constructor(
    private modelService: ModelService,
    private route: ActivatedRoute,
    private mediaService: MediaService, // Inject MediaService
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private likes: LikesService,
    private viewsService: ViewsService,
    private authService: AuthService,
    private userService: UserService
  ) {
    this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }
  private destroy$ = new Subject<void>();
  onPhoneClick(event: Event) {
    if (!this.isMobile && this.model?.phone_number) {
      event.preventDefault();
      navigator.clipboard.writeText(this.model.phone_number).then(() => {
        this.snackBar.open('Phone number copied to clipboard', 'Close', {
          duration: 2500,
        });
      });
    }
    // On mobile, default behavior (tel: link) will be used
  }
  openFullscreen(index: number) {
    this.dialog.open(FullscreenImageViewerComponent, {
      data: { images: this.images, index },
      panelClass: 'fullscreen-dialog'
    });
    const img = this.images[index];
    if (img?.id) this.viewsService.recordView('image', img.id).catch(() => {});
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.getModelDetails(id);
        this.getModelMedia(id); // Fetch images and videos
      }
    });
  }

  // method to get the model details
  getModelDetails(id: string): void {
    this.dataLoading = true;
    
    // Check if viewing own profile
    const currentUid = this.authService.getCurrentUid();
    this.isOwnProfile = currentUid === id;

    // If viewing own profile, load user data for onboarding state;
    // otherwise mark as loaded immediately so the template doesn't wait.
    if (this.isOwnProfile && currentUid) {
      this.loadUserOnboardingState(currentUid);
    } else {
      this.userOnboardingLoaded = true;
    }

    this.modelService.getModelById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        this.model = data;
        // Ensure counts have sensible defaults before media loads
        if (this.model) {
          this.model.likesCount = this.model.likesCount ?? 0;
          this.model.viewsCount = this.model.viewsCount ?? 0;
          (this.model as any).imagesCount = (this.model as any).imagesCount ?? 0;
          (this.model as any).videosCount = (this.model as any).videosCount ?? 0;
        }
        // Refresh media counts centrally to keep consistency across views
        if (this.model?.model_id) {
          this.mediaService.getUserMediaCounts(this.model.model_id)
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ imagesCount, videosCount }) => {
              if (this.model) {
                (this.model as any).imagesCount = imagesCount;
                (this.model as any).videosCount = videosCount;
              }
            });
        }
        if (this.model?.model_id) {
          this.isLiked$ = this.likes.isLiked$('model', this.model.model_id);
          // Record a view once the model data is loaded
          this.viewsService.recordView('model', this.model.model_id).catch(() => {});
        }
        this.tags = typeof data.tags === 'string' ? data.tags.split(',').map(tag => tag.trim()) : data.tags;
        this.displayedTags = this.tags.slice(0, 5);
        this.dataLoading = false;
      },
      error: () => {
        this.dataLoading = false;
        this.snackBar.open('Failed to load model', 'Close', { duration: 3000 });
      }
    });
  }


  getModelMedia(id: string): void {
    this.mediaService.getUserImages(id).pipe(
      takeUntil(this.destroy$),
      catchError(() => EMPTY)
    ).subscribe((images: Image[]) => {
      const sortedImages = [...images].sort((a, b) => {
        const aTime = a.created_at ? a.created_at.getTime() : 0;
        const bTime = b.created_at ? b.created_at.getTime() : 0;
        return bTime - aTime;
      });
      this.images = sortedImages;
      this.imageLoading = this.images.map(() => true);
      this.images.forEach(img => { this.imageLikedStates[img.id] = this.likes.isLiked$('image', img.id); });
      // Update model images count
      if (this.model) (this.model as any).imagesCount = this.images.length;
    });
    this.mediaService.getUserVideos(id).pipe(
      takeUntil(this.destroy$),
      catchError(() => EMPTY)
    ).subscribe((videos: Video[]) => {
      const sortedVideos = [...videos].sort((a, b) => {
        const aTime = a.created_at ? a.created_at.getTime() : 0;
        const bTime = b.created_at ? b.created_at.getTime() : 0;
        return bTime - aTime;
      });
      this.videos = sortedVideos;
      this.videoLoading = this.videos.map(() => true);
      this.videoPlaying = this.videos.map(() => false);
      this.videos.forEach(vid => { this.videoLikedStates[vid.id] = this.likes.isLiked$('video', vid.id); });
      // Update model videos count
      if (this.model) (this.model as any).videosCount = this.videos.length;
    });
  }

  onImageLoad(idx: number) {
    this.imageLoading[idx] = false;
  }

  onImageError(idx: number) {
    this.imageLoading[idx] = false;
  }

  onVideoLoad(idx: number) {
    this.videoLoading[idx] = false;
  }

  onVideoError(idx: number) {
    this.videoLoading[idx] = false;
  }

  onVideoPlay(video: Video) {
    if (video?.id) this.viewsService.recordView('video', video.id).catch(() => {});
  }

  playVideo(el: HTMLVideoElement, video: Video) {
    try {
      el.play();
    } catch {}
    this.onVideoPlay(video);
  }

  toggleVideoPlayback(el: HTMLVideoElement, video: Video, idx: number) {
    if (!el) return;
    if (el.paused) {
      this.playVideo(el, video);
      this.videoPlaying[idx] = true;
    } else {
      try { el.pause(); } catch {}
      this.videoPlaying[idx] = false;
    }
  }

  enterVideoFullscreen(el: HTMLVideoElement) {
    if (!el) return;
    // Enable native controls for fullscreen experience
    el.controls = true;
    const request = (el as any).requestFullscreen || (el as any).webkitRequestFullscreen || (el as any).msRequestFullscreen;
    try {
      if (request) request.call(el);
    } catch {}
    const handler = () => {
      if (!document.fullscreenElement) {
        // Disable controls when exiting fullscreen to keep custom UI inline
        el.controls = false;
        document.removeEventListener('fullscreenchange', handler);
        (document as any).webkitFullscreenChange && document.removeEventListener('webkitfullscreenchange', handler as any);
        (document as any).msfullscreenchange && document.removeEventListener('MSFullscreenChange', handler as any);
      }
    };
    document.addEventListener('fullscreenchange', handler);
    // Safari/IE legacy events fallback
    (document as any).webkitFullscreenChange && document.addEventListener('webkitfullscreenchange', handler as any);
    (document as any).msfullscreenchange && document.addEventListener('MSFullscreenChange', handler as any);
  }

  // method to toggle tags
  toggleTags(): void {
    this.showAllTags = !this.showAllTags;
    this.displayedTags = this.showAllTags ? this.tags : this.tags.slice(0, 5);
  }

  /**
   * Loads user data to determine onboarding state for banner and checklist visibility.
   */
  private loadUserOnboardingState(userId: string): void {
    this.userService.getUserById(userId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (user) => {
        if (!user) {
          this.userOnboardingLoaded = true;
          return;
        }
        this.currentUser = user;

        // Check if model onboarding is complete
        this.isModelOnboardingComplete = user.isModelOnboardingComplete();

        // Determine if banner should be shown
        this.showOnboardingBanner = user.shouldShowModelOnboardingBanner();

        // Signal that onboarding data is ready for template rendering
        this.userOnboardingLoaded = true;
      },
      error: () => {
        this.userOnboardingLoaded = true;
      }
    });
  }

  /**
   * Acknowledges and dismisses the onboarding banner.
   * Updates Firestore and hides the banner reactively.
   */
  acknowledgeOnboardingBanner(): void {
    const uid = this.authService.getCurrentUid();
    if (!uid) return;

    this.userService.acknowledgeModelOnboarding(uid).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        // Hide banner after successful write
        this.showOnboardingBanner = false;
        // Update local user state
        if (this.currentUser?.onboarding?.model) {
          this.currentUser.onboarding.model.acknowledged = true;
        }
      },
      error: () => {
        this.snackBar.open('Failed to dismiss banner', 'Close', { duration: 3000 });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleLike() {
    if (!this.model) return;
    this.likes.toggleLike('model', this.model.model_id)
      .then(() => {
        this.modelService.getModelById(this.model!.model_id).pipe(takeUntil(this.destroy$)).subscribe({
          next: updated => {
            if (this.model) this.model.likesCount = updated.likesCount;
          }
        });
      })
      .catch(err => console.error(err));
  }

  toggleImageLike(image: Image) {
    this.likes.toggleLike('image', image.id)
      .then(() => {
        this.mediaService.getImageById(image.id).pipe(takeUntil(this.destroy$)).subscribe({
          next: updated => {
            const idx = this.images.findIndex(i => i.id === updated.id);
            if (idx >= 0) this.images[idx].likesCount = updated.likesCount;
          }
        });
      })
      .catch(err => console.error(err));
  }

  toggleVideoLike(video: Video) {
    this.likes.toggleLike('video', video.id)
      .then(() => {
        this.mediaService.getVideoById(video.id).pipe(takeUntil(this.destroy$)).subscribe({
          next: updated => {
            const idx = this.videos.findIndex(v => v.id === updated.id);
            if (idx >= 0) this.videos[idx].likesCount = updated.likesCount;
          }
        });
      })
      .catch(err => console.error(err));
  }
}
